import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { UserPlus, Upload, Download, ShieldOff, Archive, History, Pencil, Trash2, ShieldCheck, CheckCircle2, XCircle, Check, ListPlus } from "lucide-react";
import { MKT, getAdminAuth } from "./AdminEmailMarketing";
import ImportDialog from "./ImportDialog";

const emptyContact = { email: "", first_name: "", last_name: "", company_name: "", phone: "", website: "", industry: "", city: "", state: "", country: "", source: "manual", notes: "", list_ids: [] };

const VERIF_COLORS = {
  valid: "bg-emerald-500/15 text-emerald-500", catch_all: "bg-sky-500/15 text-sky-400",
  risky: "bg-amber-500/15 text-amber-500", unknown: "bg-slate-500/15 text-slate-400",
  invalid: "bg-red-500/15 text-red-400", verification_failed: "bg-red-500/15 text-red-400",
  unverified: "bg-orange-500/15 text-orange-400", verifying: "bg-blue-500/15 text-blue-400",
  expired: "bg-orange-500/15 text-orange-400",
};

export default function ContactsTab({ meta, onChanged, listTarget }) {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [industry, setIndustry] = useState("all");
  const [country, setCountry] = useState("");
  const [listFilter, setListFilter] = useState("all");
  const [lists, setLists] = useState([]);
  const [page, setPage] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // contact being edited (null = add mode)
  const [deleting, setDeleting] = useState(null); // contact pending permanent delete
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState(emptyContact);
  const [timeline, setTimeline] = useState(null);
  const [verifFilter, setVerifFilter] = useState("all");
  const [supFilter, setSupFilter] = useState("all");
  const [eligFilter, setEligFilter] = useState("all");
  const [selected, setSelected] = useState([]);
  const [verifying, setVerifying] = useState(false);
  const [bulkListId, setBulkListId] = useState("");
  const limit = 25;

  useEffect(() => {
    if (listTarget?.list_id) setBulkListId(listTarget.list_id);
  }, [listTarget]);

  const fetchContacts = useCallback(async () => {
    try {
      const params = new URLSearchParams({ search, skip: page * limit, limit });
      if (industry !== "all") params.set("industry", industry);
      if (country) params.set("country", country);
      if (listFilter !== "all") params.set("list_id", listFilter);
      if (verifFilter !== "all") params.set("verification", verifFilter);
      if (supFilter !== "all") params.set("suppressed", supFilter);
      if (eligFilter !== "all") params.set("eligibility", eligFilter);
      const res = await axios.get(`${MKT}/contacts?${params}`, getAdminAuth());
      setContacts(res.data.contacts);
      setTotal(res.data.total);
      setSelected([]);
    } catch (e) { toast.error("Failed to load contacts"); }
  }, [search, industry, country, listFilter, verifFilter, supFilter, eligFilter, page]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);
  useEffect(() => {
    axios.get(`${MKT}/lists`, getAdminAuth()).then(r => setLists(r.data.lists)).catch(() => {});
  }, []);

  const addContact = async () => {
    try {
      if (editing) {
        await axios.put(`${MKT}/contacts/${editing.contact_id}`, form, getAdminAuth());
        toast.success("Contact updated");
      } else {
        await axios.post(`${MKT}/contacts`, form, getAdminAuth());
        toast.success("Contact added");
      }
      setShowAdd(false); setForm(emptyContact); setEditing(null);
      fetchContacts(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${editing ? "update" : "add"}`); }
  };

  const openEdit = (c) => {
    setForm({
      ...Object.fromEntries(Object.keys(emptyContact).map(k => [k, c[k] || ""])),
      list_ids: c.list_ids || [],
    });
    setEditing(c);
    setShowAdd(true);
  };

  const addSelectedToList = async () => {
    if (!bulkListId) return;
    try {
      const res = await axios.post(`${MKT}/lists/${bulkListId}/contacts`, { contact_ids: selected }, getAdminAuth());
      const ln = lists.find(l => l.list_id === bulkListId)?.name || "list";
      toast.success(`${res.data.added} contact(s) added to "${ln}"`
        + (res.data.added < selected.length ? ` · ${selected.length - res.data.added} already in it` : ""));
      setSelected([]);
      fetchContacts(); onChanged?.();
    } catch (e) { toast.error("Failed to add to list"); }
  };

  const removeSelectedFromList = async () => {
    try {
      const res = await axios.post(`${MKT}/lists/${listFilter}/contacts/remove`, { contact_ids: selected }, getAdminAuth());
      toast.success(`${res.data.removed} contact(s) removed from list`);
      setSelected([]);
      fetchContacts(); onChanged?.();
    } catch (e) { toast.error("Failed to remove from list"); }
  };

  const suppress = async (c) => {
    try {
      await axios.post(`${MKT}/contacts/${c.contact_id}/suppress`, {}, getAdminAuth());
      toast.success(`${c.email} suppressed`);
      fetchContacts(); onChanged?.();
    } catch (e) { toast.error("Failed"); }
  };

  const archive = async (c) => {
    try {
      await axios.put(`${MKT}/contacts/${c.contact_id}`, { status: "archived" }, getAdminAuth());
      toast.success("Archived");
      fetchContacts();
    } catch (e) { toast.error("Failed"); }
  };

  const viewTimeline = async (c) => {
    try {
      const res = await axios.get(`${MKT}/contacts/${c.contact_id}/timeline`, getAdminAuth());
      setTimeline({ contact: c, events: res.data.events });
    } catch (e) { toast.error("Failed to load timeline"); }
  };

  const deleteContact = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`${MKT}/contacts/${deleting.contact_id}`, getAdminAuth());
      toast.success(`${deleting.email} permanently deleted`);
      setDeleting(null);
      fetchContacts(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${MKT}/contacts-export`, { ...getAdminAuth(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "marketing_contacts.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const verifySelected = async () => {
    setVerifying(true);
    try {
      const res = await axios.post(`${MKT}/verification/verify`, { contact_ids: selected }, getAdminAuth());
      toast.success(`${res.data.queued} email(s) queued for verification`
        + (res.data.skipped_suppressed ? ` · ${res.data.skipped_suppressed} suppressed skipped` : "")
        + (res.data.skipped_verifying ? ` · ${res.data.skipped_verifying} already verifying` : ""));
      fetchContacts();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to queue verification"); }
    setVerifying(false);
  };

  const setF = (k) => (e) => setForm(p => ({ ...p, [k]: e.target?.value ?? e }));

  return (
    <div className="space-y-4 mt-4" data-testid="marketing-contacts-tab">
      <div className="flex flex-wrap gap-2 items-center">
        <Input placeholder="Search email, name, company…" value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="max-w-xs" data-testid="contact-search-input" />
        <Select value={industry} onValueChange={(v) => { setIndustry(v); setPage(0); }}>
          <SelectTrigger className="w-40" data-testid="contact-industry-filter"><SelectValue placeholder="Industry" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Industries</SelectItem>
            {(meta?.industries || []).map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={listFilter} onValueChange={(v) => { setListFilter(v); setPage(0); }}>
          <SelectTrigger className="w-44" data-testid="contact-list-filter"><SelectValue placeholder="List" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Lists</SelectItem>
            {lists.map(l => <SelectItem key={l.list_id} value={l.list_id}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Country (e.g. US)" value={country} onChange={(e) => { setCountry(e.target.value); setPage(0); }} className="w-32" data-testid="contact-country-filter" />
        <Select value={verifFilter} onValueChange={(v) => { setVerifFilter(v); setPage(0); }}>
          <SelectTrigger className="w-44" data-testid="contact-verification-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any Verification</SelectItem>
            {(meta?.verification_statuses || []).map(v => <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={supFilter} onValueChange={(v) => { setSupFilter(v); setPage(0); }}>
          <SelectTrigger className="w-44" data-testid="contact-suppression-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any Suppression</SelectItem>
            <SelectItem value="none">Not suppressed</SelectItem>
            <SelectItem value="true">Suppressed (any)</SelectItem>
            {(meta?.suppression_statuses || []).map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={eligFilter} onValueChange={(v) => { setEligFilter(v); setPage(0); }}>
          <SelectTrigger className="w-44" data-testid="contact-eligibility-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any Eligibility</SelectItem>
            <SelectItem value="eligible">Campaign-eligible</SelectItem>
            <SelectItem value="ineligible">Not eligible</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        {selected.length > 0 && (
          <Button size="sm" variant="outline" className="border-emerald-500/50 text-emerald-500 hover:text-emerald-400"
            onClick={verifySelected} disabled={verifying} data-testid="verify-selected-btn">
            <ShieldCheck className="w-4 h-4 mr-1" />Verify {selected.length} Selected
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-contacts-btn"><Download className="w-4 h-4 mr-1" />Export</Button>
        <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="import-contacts-btn"><Upload className="w-4 h-4 mr-1" />Import</Button>
        <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-contact-btn"><UserPlus className="w-4 h-4 mr-1" />Add Contact</Button>
      </div>

      {listTarget && (
        <div className="flex items-center gap-2 p-2.5 border border-blue-500/30 bg-blue-500/5 rounded-lg text-xs text-foreground" data-testid="list-target-banner">
          <ListPlus className="w-4 h-4 text-blue-400 shrink-0" />
          <span>Adding contacts to <b>{listTarget.name}</b> — tick the contacts below, then click "Add to list".</span>
        </div>
      )}

      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-2.5 border border-blue-500/30 bg-blue-500/5 rounded-lg" data-testid="bulk-list-bar">
          <span className="text-xs font-medium text-foreground">{selected.length} selected</span>
          <Select value={bulkListId || undefined} onValueChange={setBulkListId}>
            <SelectTrigger className="w-56 h-8" data-testid="bulk-list-select"><SelectValue placeholder="Choose a list…" /></SelectTrigger>
            <SelectContent>
              {lists.map(l => <SelectItem key={l.list_id} value={l.list_id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" disabled={!bulkListId} onClick={addSelectedToList} data-testid="bulk-add-to-list-btn">
            <ListPlus className="w-4 h-4 mr-1" />Add to list
          </Button>
          {listFilter !== "all" && (
            <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:text-red-300"
              onClick={removeSelectedFromList} data-testid="bulk-remove-from-list-btn">
              Remove from current list
            </Button>
          )}
          {lists.length === 0 && <span className="text-xs text-muted-foreground">No lists yet — create one in the Lists tab.</span>}
        </div>
      )}

      <Card className="border-border">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="contacts-table">
            <thead><tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
              <th className="p-3 w-8">
                <input type="checkbox" className="accent-emerald-500 cursor-pointer"
                  checked={contacts.length > 0 && selected.length === contacts.length}
                  onChange={(e) => setSelected(e.target.checked ? contacts.map(c => c.contact_id) : [])}
                  data-testid="select-all-contacts" />
              </th>
              <th className="p-3">Email</th><th className="p-3">Name</th><th className="p-3">Company</th>
              <th className="p-3">Industry</th><th className="p-3">Location</th><th className="p-3">Source</th>
              <th className="p-3">Verification</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {contacts.map(c => (
                <tr key={c.contact_id} className="border-b border-border/40 hover:bg-muted/30">
                  <td className="p-3">
                    <input type="checkbox" className="accent-emerald-500 cursor-pointer"
                      checked={selected.includes(c.contact_id)}
                      onChange={(e) => setSelected(p => e.target.checked ? [...p, c.contact_id] : p.filter(x => x !== c.contact_id))}
                      data-testid={`select-${c.email}`} />
                  </td>
                  <td className="p-3 font-medium text-foreground">{c.email}</td>
                  <td className="p-3">{[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}</td>
                  <td className="p-3">{c.company_name || "—"}</td>
                  <td className="p-3">{c.industry || "—"}</td>
                  <td className="p-3 text-xs">{[c.city, c.state, c.country].filter(Boolean).join(", ") || "—"}</td>
                  <td className="p-3 text-xs text-muted-foreground">{c.source || "—"}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1.5">
                      <Badge className={`text-[10px] ${VERIF_COLORS[c.effective_verification] || VERIF_COLORS.unverified}`} data-testid={`verif-${c.email}`}>
                        {(c.effective_verification || "unverified").replace(/_/g, " ")}
                      </Badge>
                      {c.send_eligible
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" title="Campaign-eligible" />
                        : <XCircle className="w-3.5 h-3.5 text-red-400/70 shrink-0" title="Not campaign-eligible" />}
                    </div>
                  </td>
                  <td className="p-3">
                    {c.suppression_status
                      ? <Badge className="bg-red-500/15 text-red-500 text-[10px]">{c.suppression_status}</Badge>
                      : <Badge className="bg-emerald-500/15 text-emerald-500 text-[10px]">active</Badge>}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)} title="Edit" data-testid={`edit-${c.email}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => viewTimeline(c)} title="Timeline" data-testid={`timeline-${c.email}`}><History className="w-3.5 h-3.5" /></Button>
                    {!c.suppression_status && (
                      <Button variant="ghost" size="sm" onClick={() => suppress(c)} title="Suppress" data-testid={`suppress-${c.email}`}><ShieldOff className="w-3.5 h-3.5 text-red-400" /></Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => archive(c)} title="Archive"><Archive className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleting(c)} title="Delete permanently" data-testid={`delete-${c.email}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                  </td>
                </tr>
              ))}
              {contacts.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-muted-foreground text-sm">No contacts</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{total} contacts</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Button>
          <Button variant="outline" size="sm" disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      </div>

      {/* Add / Edit contact */}
      <Dialog open={showAdd} onOpenChange={(o) => { setShowAdd(o); if (!o) { setEditing(null); setForm(emptyContact); } }}>
        <DialogContent className="max-w-lg" data-testid="add-contact-dialog">
          <DialogHeader><DialogTitle>{editing ? `Edit Contact — ${editing.email}` : "Add Marketing Contact"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Email *</Label><Input value={form.email} onChange={setF("email")} data-testid="new-contact-email" /></div>
            <div><Label>First name</Label><Input value={form.first_name} onChange={setF("first_name")} /></div>
            <div><Label>Last name</Label><Input value={form.last_name} onChange={setF("last_name")} /></div>
            <div className="col-span-2"><Label>Company</Label><Input value={form.company_name} onChange={setF("company_name")} /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={setF("phone")} /></div>
            <div><Label>Website</Label><Input value={form.website} onChange={setF("website")} /></div>
            <div>
              <Label>Industry</Label>
              <Select value={form.industry || undefined} onValueChange={(v) => setForm(p => ({ ...p, industry: v }))}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(meta?.industries || []).map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>City</Label><Input value={form.city} onChange={setF("city")} /></div>
            <div><Label>State</Label><Input value={form.state} onChange={setF("state")} /></div>
            <div><Label>Country</Label><Input value={form.country} onChange={setF("country")} /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={setF("notes")} /></div>
            {lists.length > 0 && (
              <div className="col-span-2">
                <Label>Lists</Label>
                <div className="flex flex-wrap gap-2 mt-1.5" data-testid="contact-lists-picker">
                  {lists.map(l => {
                    const on = (form.list_ids || []).includes(l.list_id);
                    return (
                      <button
                        key={l.list_id} type="button"
                        onClick={() => setForm(p => ({
                          ...p,
                          list_ids: on ? (p.list_ids || []).filter(x => x !== l.list_id) : [...(p.list_ids || []), l.list_id],
                        }))}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition-colors ${on
                          ? "bg-blue-500/20 border-blue-500/50 text-blue-400"
                          : "border-border text-muted-foreground hover:border-blue-500/40"}`}
                        data-testid={`contact-list-chip-${l.name}`}
                      >
                        {on && <Check className="w-3 h-3" />}{l.name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Click to toggle — this contact will be a member of the highlighted lists.</p>
              </div>
            )}
          </div>
          <Button onClick={addContact} data-testid="save-contact-btn">{editing ? "Save Changes" : "Add Contact"}</Button>
        </DialogContent>
      </Dialog>

      <ImportDialog open={showImport} onOpenChange={setShowImport} lists={lists}
        onImported={() => { fetchContacts(); onChanged?.(); }} />

      {/* Permanent delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-contact-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete this contact?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-foreground">{deleting?.email}</span> will be removed from all lists along with its activity history. This cannot be undone.
              Unlike Archive, the contact disappears completely — use this for contacts added by mistake.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-contact">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteContact} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-contact">
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Timeline */}
      <Dialog open={!!timeline} onOpenChange={() => setTimeline(null)}>
        <DialogContent className="max-w-md max-h-[70vh] overflow-y-auto" data-testid="contact-timeline-dialog">
          <DialogHeader><DialogTitle className="text-sm">Timeline — {timeline?.contact?.email}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {(timeline?.events || []).map(ev => (
              <div key={ev.event_id} className="flex items-start gap-2 text-xs border-b border-border/40 pb-2">
                <Badge className="bg-blue-500/15 text-blue-400 text-[10px] shrink-0">{ev.type}</Badge>
                <div className="min-w-0">
                  <p className="text-muted-foreground">{new Date(ev.created_at).toLocaleString()}</p>
                  {ev.campaign_id && <p className="text-[10px] text-muted-foreground/70 font-mono">{ev.campaign_id}</p>}
                </div>
              </div>
            ))}
            {(timeline?.events || []).length === 0 && <p className="text-xs text-muted-foreground">No events yet</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
